import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { separateNextUndoStep } from '../../../DocEditor/utils/undoSeparation';
import { wrapMathValueToken } from '../../../../utils/mathValueToken';
import { hasMathSlash, isMathSlashDraft } from '../../mathSlash';
import { useFieldMathMenu } from '../../useFieldMathMenu';
import { useValueSlashMenu } from '../../useValueSlashMenu';
import { NUMBER_VALUE_SLASH_IDS, slashQueryFrom, stripSlashQuery } from '../../valueSlash';
import '../../../DocEditor/styles/slash-menu.css';

/**
 * Local text field — paints on every key locally; commits only on blur / Enter
 * so the host (and variable resolution) doesn't recompute per keystroke.
 */
const LocalField = memo(({
  value,
  placeholder,
  className,
  style,
  ariaLabel,
  inputRef,
  readOnly,
  transform,
  onCommit,
  onFocus,
  spellCheck = false,
  inputMode,
  autoFocus = false,
  historyEpoch = 0,
  resolveVariable = null,
  pickerVariables = [],
  onCreateVariable = null,
  allowMath = false,
}) => {
  const localRef = useRef(null);
  const ref = inputRef || localRef;
  const focusedRef = useRef(false);
  const [text, setText] = useState(value || '');
  const textRef = useRef(text);

  const writeText = (next) => {
    textRef.current = next;
    setText(next);
  };

  useEffect(() => {
    if (focusedRef.current) return;
    const next = value || '';
    textRef.current = next;
    setText(next);
  }, [value]);

  // DocEditor undo/redo bumps historyEpoch — adopt restored value even while focused
  useEffect(() => {
    const next = value || '';
    textRef.current = next;
    setText(next);
  }, [historyEpoch]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional

  const insertMathExpr = useCallback(
    (expr) => {
      const token = wrapMathValueToken(expr);
      textRef.current = token;
      setText(token);
      focusedRef.current = false;
      onCommit?.(token, { silent: false });
    },
    [onCommit]
  );

  const { mathMenu, openMathMenu, mathMenuOpenRef } = useFieldMathMenu({
    resolveVariable,
    availableVariables: pickerVariables,
    onCreateVariable,
    onInsertExpr: insertMathExpr,
    onRestoreFocus: () => ref.current?.focus(),
  });

  const fieldMenuPosition = () => {
    const rect = ref.current?.getBoundingClientRect();
    return {
      x: rect?.left || 0,
      y: (rect?.bottom || 0) + 4,
      caretTop: rect?.top || 0,
    };
  };

  const openMathFromField = () => {
    if (readOnly || !allowMath) return;
    writeText(stripSlashQuery(textRef.current));
    openMathMenu(fieldMenuPosition());
  };

  const { slashMenu, slashOpenRef, openAt, setQuery, close } = useValueSlashMenu({
    allowedIds: allowMath ? NUMBER_VALUE_SLASH_IDS : null,
    readOnly,
    onPick: (id) => {
      if (id === 'math') openMathFromField();
    },
    onDismiss: () => writeText(stripSlashQuery(textRef.current)),
  });

  const commitAndBlur = () => {
    if (readOnly) return;
    ref.current?.blur();
  };

  return (
    <>
      <textarea
        ref={ref}
        rows={1}
        className={`variables-panel__input variables-panel__textarea ${className || ''}`}
        style={style}
        value={text}
        placeholder={placeholder}
        readOnly={readOnly}
        spellCheck={spellCheck}
        lang={spellCheck ? undefined : 'zxx'}
        inputMode={inputMode}
        autoFocus={autoFocus}
        autoCorrect={spellCheck ? undefined : 'off'}
        autoCapitalize={spellCheck ? undefined : 'off'}
        tabIndex={readOnly ? -1 : undefined}
        onFocus={() => {
          focusedRef.current = true;
          onFocus?.();
        }}
        onChange={(e) => {
          if (readOnly) return;
          const raw = e.target.value;
          if (slashOpenRef.current) {
            writeText(raw);
            const query = slashQueryFrom(raw);
            if (query == null) close();
            else setQuery(query);
            if (hasMathSlash(raw)) {
              close();
              openMathFromField();
            }
            return;
          }
          if (allowMath && hasMathSlash(raw)) {
            writeText(raw);
            openMathFromField();
            return;
          }
          if (allowMath && isMathSlashDraft(raw)) {
            writeText(raw);
            openAt(fieldMenuPosition());
            setQuery(slashQueryFrom(raw) || '');
            return;
          }
          writeText(transform ? transform(raw) : raw);
        }}
        onKeyDown={(e) => {
          if (readOnly) return;
          if (mathMenuOpenRef.current) return;
          if (allowMath && e.key === '/' && !slashOpenRef.current) {
            e.preventDefault();
            const el = e.target;
            const start = el.selectionStart ?? textRef.current.length;
            const end = el.selectionEnd ?? start;
            const current = textRef.current;
            writeText(`${current.slice(0, start)}/${current.slice(end)}`);
            openAt(fieldMenuPosition());
            requestAnimationFrame(() => {
              el.setSelectionRange(start + 1, start + 1);
            });
            return;
          }
          if (slashOpenRef.current) return;
          if (e.key === 'Enter') {
            e.preventDefault();
            if (allowMath && hasMathSlash(textRef.current)) {
              openMathFromField();
              return;
            }
            commitAndBlur();
          }
        }}
        onPaste={() => {
          if (readOnly) return;
          separateNextUndoStep();
        }}
        onCut={() => {
          if (readOnly) return;
          separateNextUndoStep();
        }}
        onBlur={() => {
          if (mathMenuOpenRef.current || slashOpenRef.current) return;
          focusedRef.current = false;
          if (readOnly) return;
          onCommit?.(textRef.current, { silent: false });
        }}
        aria-label={ariaLabel}
        aria-readonly={readOnly || undefined}
      />
      {slashMenu}
      {mathMenu}
    </>
  );
});

export default LocalField;
